import * as AES from './AES';
import * as HMAC from './HMAC';
import * as SHA from './SHA';
import { VByteOperationType } from '@datarock/protobuffers';
const encoder = new TextEncoder();
export async function createAESDataKeyFromTransient(serverSeed, seedB, encryptionGUID) {
    if (!serverSeed.bitwiseOperations) {
        throw new Error('Bitwise operations are required');
    }
    const guidBytes = encoder.encode(encryptionGUID);
    const salt = await createSaltForHMAC(serverSeed.iv, guidBytes);
    const params = {
        bitwiseOperations: serverSeed.bitwiseOperations,
        guid: guidBytes,
        salt,
        seedA: serverSeed.seed,
        seedB: seedB
    };
    return createDataKey(params);
}
async function createDataKey(options) {
    const keyBytes = await deriveDataKey(options);
    return AES.importKey(keyBytes);
}
export async function createAESWrappingKeyFromTransient(serverSeed, encryptionGUID) {
    const guidBytes = encoder.encode(encryptionGUID);
    const salt = await createSaltForHMAC(serverSeed.iv, guidBytes);
    const params = {
        salt,
        seedA: serverSeed.seed
    };
    return createWrappingKey(params);
}
async function createWrappingKey(options) {
    const keyBytes = await deriveKeyUsingHMAC(options.seedA, options.salt, 32);
    return AES.importKey(keyBytes);
}
async function createSaltForHMAC(iv, encryptionGUID) {
    const combined = Uint8Array.from([...iv, ...encryptionGUID]);
    return SHA.hash(combined);
}
async function deriveDataKey(options) {
    const combined = Uint8Array.from([...options.seedA, ...options.seedB]);
    const seedC = await SHA.hash(combined);
    processOperations(seedC, options.bitwiseOperations);
    return deriveKeyUsingHMAC(seedC, options.salt, 32);
}
async function deriveKeyUsingHMAC(inputKey, salt, outputLength) {
    const prk = await HMAC.sha256(salt, inputKey);
    const okm = new Uint8Array(outputLength);
    let previousBlock = new Uint8Array(0);
    let bytesGenerated = 0;
    let counter = 1;
    while (bytesGenerated < outputLength) {
        const data = new Uint8Array([...previousBlock, counter]);
        const hmac = await HMAC.sha256(prk, data);
        previousBlock = new Uint8Array(hmac);
        // Copy generated bytes into output
        const remainingBytes = outputLength - bytesGenerated;
        const bytesToCopy = Math.min(remainingBytes, hmac.length);
        okm.set(hmac.subarray(0, bytesToCopy), bytesGenerated);
        bytesGenerated += bytesToCopy;
        counter++;
    }
    return okm;
}
function getSwapIndex(length, currentIndex, swapLeftCount) {
    const swapIndex = currentIndex - (swapLeftCount % length);
    if (swapIndex < 0) {
        return length + swapIndex;
    }
    return swapIndex;
}
function swapBytes(data, index1, index2) {
    const temp = data[index1];
    data[index1] = data[index2];
    data[index2] = temp;
}
function processOperations(data, operationsList) {
    let opCount = 0;
    for (let i = 0; i < operationsList.rounds; i++) {
        for (let j = 0; j < data.length; j++) {
            const compareByte = j === 0 ? data[data.length - 1] : data[j - 1];
            switch (operationsList.operations[opCount].operationType) {
                case VByteOperationType.AND:
                    data[j] = data[j] & compareByte;
                    break;
                case VByteOperationType.OR:
                    data[j] = data[j] | compareByte;
                    break;
                case VByteOperationType.XOR:
                    data[j] = data[j] ^ compareByte;
                    break;
                case VByteOperationType.COMPLEMENT:
                    data[j] = ~data[j];
                    break;
                case VByteOperationType.SUB:
                    data[j] = data[j] | operationsList.operations[opCount].subBytes[0];
                    break;
                case VByteOperationType.SWAP:
                    const swapIndex = getSwapIndex(data.length, j, operationsList.operations[opCount].swapLeftCount);
                    swapBytes(data, j, swapIndex);
                    break;
                case VByteOperationType.ROTATELEFT:
                    data[j] = rotateLeft(data[j], operationsList.operations[opCount].rotateCount);
                    break;
                case VByteOperationType.ROTATERIGHT:
                    data[j] = rotateRight(data[j], operationsList.operations[opCount].rotateCount);
                    break;
            }
            opCount++;
            if (opCount === operationsList.operations.length) {
                opCount = 0;
            }
        }
    }
    return opCount;
}
function rotateLeft(value, shift) {
    return (value << shift) | (value >> (8 - shift));
}
function rotateRight(value, shift) {
    return (value >> shift) | (value << (8 - shift));
}
